import {
  Button,
  FeedCardGrid,
  FeedCardGridItem,
  Icon,
  TextInput,
  FilterBar,
  Filter as FilterBarFilter,
  FilterBarValue,
} from '@socialchorus/shared-ui-components';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'usehooks-ts';
import { DEFAULT_SKIP_TO_CONTENT_ID } from '../../../../../common/use-skip-to-content';
import EmptyFeed from '../../../../feed/empty-feed';
import EndOfFeed from '../../../../feed/end-of-feed';
import { Spinner } from '../../../../ui';
import ViewTrigger from '../../../../view-trigger';
import { BaseTab } from '../BaseTab';
import { FeedCardWithData } from '../../../../content/solar-feed-card/solar-feed-card';
import styles from './styles.module.scss';
import EmptyPlaceholder from '../../common/EmptyPlaceholder';
import useFeatureFlag from '../../../../../common/use-feature-flag';
import { Feature } from '../../../../../models/features/features';
import { useChannelSearchMeta } from '../../../../../common/queries/use-channel-search-meta';
import { useContentFeedQuery } from '../../../../../common/queries/use-content-feed-query';
import { convertToFilterBarFilters } from './util';

interface Props {
  topicId: string;
  analyticsData: IAnalyticsData;
  activeTabId: number;
}

type SearchFilters = Record<
  string,
  string | string[] | Record<string, string | string[]>
>;

const STANDARD_FILTERS = [
  'authors',
  'sort',
  'from',
  'to',
  'contains',
  'labels',
];

const PostsTab: FC<Props> = ({ topicId, analyticsData, activeTabId }) => {
  const { t } = useTranslation();

  const [hasLoaded, setHasLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const resetSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };

  const isFiltersEnabled = useFeatureFlag(
    Feature.KNOWLEDGE_HUB_FILTERS_ENABLED
  );
  const [filters, setFilters] = useState<FilterBarFilter[]>([]);
  const [defaultFilters, setDefaultFilters] = useState<string[]>([]);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const { data: metadata } = useChannelSearchMeta(topicId); // useCategoryMetadata();

  useEffect(() => {
    if (metadata) {
      const topicMetadata = metadata.categories[0];
      if (topicMetadata?.filters) {
        setFilters(convertToFilterBarFilters(topicMetadata.filters));
        setDefaultFilters(topicMetadata.defaultFilters || []);
      }
    }
  }, [metadata]);

  const handleFilterUpdate = (selectedFilters: FilterBarValue[]) => {
    if (selectedFilters.length === 0) {
      setSearchFilters({});
      resetSearch();
      return;
    }
    const newSearchFilters: SearchFilters = {};
    selectedFilters.forEach((filterValue) => {
      if (filterValue.parameter === 'search') {
        setSearchQuery(filterValue.value as string);
      } else {
        // Special handling for 'from, to' date range filter
        if (
          filterValue.value instanceof Object &&
          'start' in filterValue.value &&
          'end' in filterValue.value
        ) {
          newSearchFilters['from'] = (
            filterValue.value as Record<string, Date>
          ).start.toString();
          newSearchFilters['to'] = (
            filterValue.value as Record<string, Date>
          ).end.toString();
          return;
        }
        if (
          (filterValue.value instanceof Array &&
            filterValue.value.length > 0) ||
          (typeof filterValue.value === 'string' && filterValue.value !== '')
        ) {
          if (STANDARD_FILTERS.includes(filterValue.parameter)) {
            newSearchFilters[filterValue.parameter] = filterValue.value;
          } else {
            // For non-standard filters, use `filters[parameter]` as the key and allow axios to handle array serialization.
            newSearchFilters[`filters[${filterValue.parameter}]`] =
              filterValue.value;
          }
        }
      }
    });
    setSearchFilters(newSearchFilters);
  };

  const {
    isLoading,
    isFetchingNextPage,
    data: feedItems,
    rawQueryData,
    hasNextPage: canLoadMore,
    fetchNextPage: fetchFeed,
  } = useContentFeedQuery({
    channelId: topicId,
    // If the search query has been cleared, use the empty value to prevent delay
    query: searchQuery.length === 0 ? searchQuery : debouncedSearchQuery,
    pinned: true,
    homepageModuleParams: searchFilters,
  });

  const fetching = isLoading || isFetchingNextPage;

  // Only the search endpoint reports a match count, so this is defined only while a
  // search query is active. The filters-only case goes to the feed endpoint instead,
  // which reports has_more rather than a total.
  const total = rawQueryData?.pages?.[0]?.meta.total;
  // An explicit empty array, not just a falsy length: feedItems is undefined until the
  // query resolves and stays undefined when it errors, and neither of those is "no
  // results" -- claiming it would replace a failed search with a "nothing matched"
  // placeholder.
  const hasNoResults = feedItems?.length === 0;

  useEffect(() => {
    if (!hasLoaded && !fetching) {
      setHasLoaded(true);
    }
  }, [fetching, hasLoaded]);

  // If the search query has been cleared, set this variable to `false` instead of waiting for the debounced value
  const hasQuery =
    (searchQuery.length > 0 && debouncedSearchQuery.length > 0) ||
    Object.keys(searchFilters).length > 0;
  const hasNoPostsInTopic =
    !hasQuery && !fetching && feedItems && feedItems.length === 0;

  if (hasNoPostsInTopic) {
    return (
      <BaseTab activeTabId={activeTabId} topicId={topicId}>
        <EmptyPlaceholder
          title={t('screens.topic_page.no_posts.title')}
          description={t('screens.topic_page.no_posts.description')}
        />
      </BaseTab>
    );
  }

  return (
    <BaseTab activeTabId={activeTabId} topicId={topicId}>
      {/* Hide search box before first load and only display when there's content in the topic */}
      {hasLoaded && !isFiltersEnabled && (
        <div className={styles.SearchBox__Wrapper}>
          <TextInput
            type="search"
            ref={searchInputRef}
            leftWidget={<Icon size={14}>search</Icon>}
            placeholder={t('screens.topic_page.searchbox_placeholder')}
            size="compact"
            value={searchQuery}
            onChange={(val) => setSearchQuery(val || '')}
          />
          {hasQuery && !fetching && (
            <span
              role="status"
              aria-live="polite"
              className="search-box--result-status"
            >
              {t('screens.topic_page.search_results.count', { count: total })}
            </span>
          )}
        </div>
      )}
      {hasLoaded && isFiltersEnabled && (
        <FilterBar
          searchEnabled
          dropdownArrowPosition="right"
          variant="compact"
          filterSearchEnabled
          className={styles.FilterBar}
          filters={filters}
          defaultFilterIds={defaultFilters}
          onUpdate={handleFilterUpdate}
          dropdownClassName={styles.FilterBar__Dropdown}
        />
      )}

      {!fetching && hasQuery && hasNoResults ? (
        <EmptyPlaceholder
          title={t('screens.topic_page.search_results.no_results.title')}
          description={t(
            'screens.topic_page.search_results.no_results.description',
            {
              query: debouncedSearchQuery,
            }
          )}
          cta={
            <Button
              variant="primary"
              label={t('content.clear_search')}
              onClick={resetSearch}
            />
          }
        />
      ) : (
        <section
          className={styles.FeedCardList}
          id={DEFAULT_SKIP_TO_CONTENT_ID}
        >
          <FeedCardGrid columns={1}>
            {feedItems?.map((content) => (
              <FeedCardGridItem key={content.id}>
                <FeedCardWithData
                  content={content}
                  analyticsData={analyticsData}
                  layout="horizontal"
                  showPinnedLabel
                />
              </FeedCardGridItem>
            ))}
          </FeedCardGrid>
          {fetching ? (
            <Spinner />
          ) : canLoadMore ? (
            <ViewTrigger offset="600px" onInview={() => fetchFeed()} />
          ) : feedItems?.length ? (
            <EndOfFeed />
          ) : (
            <EmptyFeed />
          )}
        </section>
      )}
    </BaseTab>
  );
};

export default PostsTab;
